/**
 * Отчёт по операциям: отображение колонок A–P в соответствии с Talend.
 * Источник: promts/revert 2nd tab.md §2, columns_in_excel.md
 *
 * API: GET /report-constructor-data → data.operations[]
 */

export interface OperationReportApiOperation {
  id: number;
  date?: string;
  time_interval?: string;
  size?: number | string;
  actual_size?: number | string;
  quantity?: number | string;
  actual_quantity?: number | string;
  actual_work_time?: number | string;
  tariff?: number | string;
  application?: {
    id?: number | string;
    platform?: { name?: string };
    Supplier?: { legal_name?: string };
    client?: { legal_name?: string };
    status?: { name?: string };
  };
  workType?: {
    name?: string;
    work_billing_type?: { name?: string };
    cost_center?: { name?: string };
  };
}

/** Округление как в Talend tJavaRow_3: round(x * 100) / 100 */
export function roundLikeTalend(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return Math.round(n * 100) / 100;
}

/** Колонки I–M: две десятичные, 0 отображается как 0,00 */
export function formatMetricTalend(v: unknown): string {
  const r = roundLikeTalend(v);
  if (r === null) return '';
  return r.toFixed(2);
}

/** Колонка N: две десятичные; 0 и отсутствие значения → пустая ячейка */
export function formatTariffTalend(v: unknown): string {
  const r = roundLikeTalend(v);
  if (r === null || r === 0) return '';
  return r.toFixed(2);
}

export function parseDateIso(raw: unknown): string {
  if (raw === null || raw === undefined || raw === '') return '';
  const s = String(raw).trim().replace(' 00:00:00', '');
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return d.toISOString().slice(0, 10);
  }
  return '';
}

/** Колонка E: YYYY-MM-DD из БД → DD.MM.YYYY */
export function formatDateDdMmYyyy(raw: unknown): string {
  const iso = parseDateIso(raw);
  if (!iso) return '';
  const [y, mo, day] = iso.split('-');
  return `${day}.${mo}.${y}`;
}

export interface OperationReportTableRow {
  id: number;
  applicationId: string | number;
  platform: string;
  supplier: string;
  client: string;
  /** DD.MM.YYYY */
  date: string;
  /** YYYY-MM-DD для сортировки */
  dateIso: string;
  shift: string;
  workType: string;
  billing: string;
  size: string;
  actualSize: string;
  quantity: string;
  actualQuantity: string;
  actualWorkTime: string;
  tariff: string;
  costCenter: string;
  status: string;
  _size: number;
  _actualSize: number;
  _quantity: number;
  _actualQuantity: number;
  _actualWorkTime: number;
  _tariff: number | null;
}

export function mapOperationToTableRow(op: OperationReportApiOperation): OperationReportTableRow {
  const dateIso = parseDateIso(op.date);
  return {
    id: op.id,
    applicationId: op.application?.id ?? '',
    platform: op.application?.platform?.name ?? '',
    supplier: op.application?.Supplier?.legal_name ?? '',
    client: op.application?.client?.legal_name ?? '',
    date: formatDateDdMmYyyy(op.date),
    dateIso,
    shift: op.time_interval === 'day' ? 'День' : 'Ночь',
    workType: op.workType?.name ?? '',
    billing: op.workType?.work_billing_type?.name ?? '',
    size: formatMetricTalend(op.size),
    actualSize: formatMetricTalend(op.actual_size),
    quantity: formatMetricTalend(op.quantity),
    actualQuantity: formatMetricTalend(op.actual_quantity),
    actualWorkTime: formatMetricTalend(op.actual_work_time),
    tariff: formatTariffTalend(op.tariff),
    costCenter: op.workType?.cost_center?.name ?? '',
    status: op.application?.status?.name ?? '',
    _size: roundLikeTalend(op.size) ?? 0,
    _actualSize: roundLikeTalend(op.actual_size) ?? 0,
    _quantity: roundLikeTalend(op.quantity) ?? 0,
    _actualQuantity: roundLikeTalend(op.actual_quantity) ?? 0,
    _actualWorkTime: roundLikeTalend(op.actual_work_time) ?? 0,
    _tariff: roundLikeTalend(op.tariff),
  };
}
